// Istanbul calendar math (docs/DATABASE.md → Conventions). Pure: safe for browser code too,
// which imports this file directly (`@hedefuni/domain/time`).
//
// Turkey has used UTC+3 all year since 2016 (no daylight saving), so Istanbul local time is
// converted with a fixed offset.

export const ISTANBUL_OFFSET_MINUTES = 180;

const DAY_MS = 24 * 60 * 60 * 1000;

/** `YYYY-MM-DD` of an instant in Istanbul. */
export function istanbulDate(instant: Date): string {
  return new Date(instant.getTime() + ISTANBUL_OFFSET_MINUTES * 60_000).toISOString().slice(0, 10);
}

/** UTC instant for an Istanbul calendar date and minutes from midnight. */
export function istanbulToUtc(date: string, minuteOfDay: number): Date {
  const midnightUtc = Date.parse(`${date}T00:00:00Z`);
  return new Date(midnightUtc + (minuteOfDay - ISTANBUL_OFFSET_MINUTES) * 60_000);
}

export function addDays(date: string, days: number): string {
  return new Date(Date.parse(`${date}T00:00:00Z`) + days * DAY_MS).toISOString().slice(0, 10);
}

/** ISO day of week: 1 = Monday … 7 = Sunday. */
export function isoDayOfWeek(date: string): number {
  const day = new Date(`${date}T00:00:00Z`).getUTCDay();
  return day === 0 ? 7 : day;
}

const HOUR_MS = 60 * 60 * 1000;
const WEEK_MS = 7 * DAY_MS;

/**
 * Where a subscription's weekly hour cycles start (product decision 2026-10-12): the purchase,
 * rounded up to the next full hour, so resets spread over the week and the day (a package bought
 * on Wednesday at 14:37 resets every Wednesday at 15:00). Kept through renewals and upgrades.
 */
export function weekAnchorFor(purchasedAt: Date): Date {
  return new Date(Math.ceil(purchasedAt.getTime() / HOUR_MS) * HOUR_MS);
}

/**
 * Start of the weekly hour cycle that contains `instant` (docs/MVP.md → Plans and
 * Subscriptions). The minutes between the purchase and the anchor belong to the first cycle,
 * which is therefore slightly longer than a week.
 */
export function weekStartAt(instant: Date, anchor: Date): Date {
  const weeks = Math.max(0, Math.floor((instant.getTime() - anchor.getTime()) / WEEK_MS));
  return new Date(anchor.getTime() + weeks * WEEK_MS);
}

/** When the cycle that contains `instant` ends and the weekly hours come back. */
export function weekEndAt(instant: Date, anchor: Date): Date {
  return new Date(weekStartAt(instant, anchor).getTime() + WEEK_MS);
}
